import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, Input, OnInit, OnDestroy, ElementRef, inject, computed } from '@angular/core';
import { ProfileService } from 'src/app/services/profile/profile.service';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { LabelsService } from 'src/app/util/services/labels/labels.service';

@Component({
	standalone: true,
	imports: [CommonModule],
	selector: 'date-range-picker',
	templateUrl: './date-range-picker.component.html',
	styleUrls: ['./date-range-picker.component.scss'],
})
export class DateRangePickerComponent implements OnInit, OnDestroy {
	private readonly labelsService = inject(LabelsService);
	private readonly elementRef = inject(ElementRef);
	labels: LabelsInterface;

	currentDate = new Date();
	@Input() showCalendar = true;

	startDate: Date | null = null;
	endDate: Date | null = null;

	viewCurrentMonth: number = this.currentDate.getMonth();
	viewCurrentYear: number = this.currentDate.getFullYear();
	viewCurrentMonthDays: (number | null)[] = [];
	hoveredDate: Date | null = null;

	daysOfWeekMap: { [key: string]: string[] } = {
		portuguese: ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'],
		english: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
		spanish: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'],
		slovak: ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'],
		chinese: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
		italian: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'],
	};
	daysOfWeek: string[] = [];
	startMonthYear = '';
	endMonthYear = '';

	@Input() initialStartDate: Date | null = null;
	@Input() initialEndDate: Date | null = null;

	@Output() datesConfirmed = new EventEmitter<string[]>(); // Changed to emit a string array
	@Output() calendarClosed = new EventEmitter<void>(); // Evento para fechar o calendário

	@Input() showTime = true;
	@Input() limitDateToday = true;

	@Input() xPositon: 'left' | 'right' = 'left';
	@Input() yPosition: 'top' | 'bottom' = 'top';

	private documentClickListener?: (event: Event) => void;

	constructor(
		public ProfileService: ProfileService,
		private elementReference: ElementRef
	) {
		this.labels = computed(() => this.labelsService.labels())();
	}

	ngOnInit() {
		this.startDate = this.initialStartDate;
		this.endDate = this.initialEndDate;
		this.updateCalendar();

		const userLanguage = this.ProfileService.userData().language?.toLowerCase() || 'english';
		this.daysOfWeek = this.daysOfWeekMap[userLanguage] || this.daysOfWeekMap['english'];

		// Adiciona listener para detectar cliques fora do componente com delay
		setTimeout(() => {
			this.documentClickListener = this.onDocumentClick.bind(this);
			document.addEventListener('click', this.documentClickListener);
		}, 100);
	}

	ngOnDestroy() {
		// Remove o listener quando o componente for destruído
		if (this.documentClickListener) {
			document.removeEventListener('click', this.documentClickListener);
		}
	}

	// Detecta cliques fora do componente
	private onDocumentClick(event: Event): void {
		const target = event.target as HTMLElement;

		// Verifica se o clique foi em um ícone de calendário (para evitar fechar imediatamente após abrir)
		if (target.closest('fa-icon') || target.closest('.fa-calendar')) {
			return;
		}

		if (this.showCalendar && !this.elementRef.nativeElement.contains(target)) {
			this.cancelAndClose();
		}
	}

	// Método para cancelar, limpar datas e fechar o calendário
	cancelAndClose(): void {
		this.clearDates();
		this.calendarClosed.emit();
	}

	// Método para limpar todas as datas selecionadas
	clearDates(): void {
		this.startDate = null;
		this.endDate = null;
		this.viewCurrentMonth = this.currentDate.getMonth();
		this.viewCurrentYear = this.currentDate.getFullYear();
		this.updateCalendar();
	}

	// Formata a data máxima para o input de Data Inicial (último dia do mês atual)
	get maxStartDateFormatted(): string {
		const year = this.currentDate.getFullYear();
		const month = String(this.currentDate.getMonth() + 1).padStart(2, '0');
		const lastDay = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0).getDate();
		return `${year}-${month}-${lastDay}`;
	}

	// Formata a data mínima para o input de Data Final (sem restrição de mês)
	get minEndDateFormatted(): string {
		const year = this.currentDate.getFullYear() - 10; // Permite até 10 anos atrás
		const month = '01';
		const day = '01';
		return `${year}-${month}-${day}`;
	}

	// Verifica se o botão Confirmar deve estar desativado
	isConfirmDisabled(): boolean {
		if (!this.startDate) {
			return true; // Desativa se startDate estiver vazio
		}
		if (this.endDate && this.startDate > this.endDate) {
			return true; // Desativa se startDate for maior que endDate
		}
		return false; // Habilita se apenas startDate estiver selecionado ou se o intervalo for válido
	}

	// Verifica se o mês anterior está desativado
	isPreviousMonthDisabled(type: 'start' | 'end'): boolean {
		return false; // Permite navegar para meses anteriores em ambos os calendários
	}

	isDateAfterToday(date: Date): boolean {
		const today = new Date();
		today.setHours(23, 59, 59, 999);
		return date > today;
	}

	updateCalendar() {
		this.viewCurrentMonthDays = this.getDaysInMonth(this.viewCurrentMonth, this.viewCurrentYear);
		this.startMonthYear = this.getMonthName(this.viewCurrentMonth) + ' ' + this.viewCurrentYear;
	}

	getDaysInMonth(month: number, year: number): (number | null)[] {
		const date = new Date(year, month, 1);
		const days: (number | null)[] = [];

		const firstDay = date.getDay();
		const offset = firstDay === 0 ? 6 : firstDay - 1;
		for (let index = 0; index < offset; index++) {
			days.push(null);
		}

		while (date.getMonth() === month) {
			days.push(date.getDate());
			date.setDate(date.getDate() + 1);
		}

		return days;
	}

	getMonthName(month: number): string {
		const monthNamesMap: { [key: string]: string[] } = {
			portuguese: [
				'Janeiro',
				'Fevereiro',
				'Março',
				'Abril',
				'Maio',
				'Junho',
				'Julho',
				'Agosto',
				'Setembro',
				'Outubro',
				'Novembro',
				'Dezembro',
			],
			english: [
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
			],
			spanish: [
				'Enero',
				'Febrero',
				'Marzo',
				'Abril',
				'Mayo',
				'Junio',
				'Julio',
				'Agosto',
				'Septiembre',
				'Octubre',
				'Noviembre',
				'Diciembre',
			],
			slovak: ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December'],
			chinese: ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'],
			italian: [
				'Gennaio',
				'Febbraio',
				'Marzo',
				'Aprile',
				'Maggio',
				'Giugno',
				'Luglio',
				'Agosto',
				'Settembre',
				'Ottobre',
				'Novembre',
				'Dicembre',
			],
		};

		const userLanguage = this.ProfileService.userData().language?.toLowerCase() || 'english';
		const monthNames = monthNamesMap[userLanguage] || monthNamesMap['english'];

		if (month < 0 || month > 11) {
			console.warn('Mês inválido:', month);
			return '';
		}

		return monthNames[month];
	}

	changeMonth(direction: number) {
		this.viewCurrentMonth += direction;
		if (this.viewCurrentMonth < 0) {
			this.viewCurrentMonth = 11;
			this.viewCurrentYear--;
		} else if (this.viewCurrentMonth > 11) {
			this.viewCurrentMonth = 0;
			this.viewCurrentYear++;
		}
		this.updateCalendar();
	}

	selectDate(day: number | null) {
		const clickedDate = new Date(this.viewCurrentYear, this.viewCurrentMonth, day!);
		if (!this.startDate) {
			this.startDate = clickedDate;
		} else if (this.endDate) {
			if (this.areSameDate(this.startDate, this.endDate) && this.areSameDate(clickedDate, this.startDate)) {
				this.hoveredDate = this.startDate;
				this.startDate = null;
				this.endDate = null;
			} else if (this.areSameDate(clickedDate, this.startDate)) {
				this.hoveredDate = this.startDate;
				this.startDate = null;
			} else if (this.areSameDate(clickedDate, this.endDate)) {
				this.hoveredDate = this.endDate;
				this.endDate = null;
			} else {
				this.hoveredDate = clickedDate;
				this.startDate = clickedDate;
				this.endDate = null;
			}
		} else {
			this.endDate = clickedDate;
			this.endDate.setHours(23, 59, 59, 999);
		}
	}

	handleHover(day: number, month: number, year: number) {
		if (this.startDate && this.endDate) return;
		this.hoveredDate = this.getDate(day, month, year);
		if (!this.endDate && this.hoveredDate < this.startDate!) {
			this.endDate = this.startDate;
			this.endDate.setHours(23, 59, 59, 999);
			this.startDate = null;
		}
		if (!this.startDate && this.endDate && this.hoveredDate > this.endDate) {
			this.startDate = this.endDate;
			this.startDate.setHours(0, 0, 0, 0);
			this.endDate = null;
		}
	}

	areSameDate(date1: Date | null, date2: Date | null): boolean {
		if (!date1 || !date2) return false;
		return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
	}

	isStartDateSelected(day: number | null, month: number | null, year: number | null): boolean {
		if (!day || !month || !year || !this.startDate) return false;
		const currentDate = new Date(year, month, day);
		return this.areSameDate(currentDate, this.startDate);
	}

	isEndDateSelected(day: number | null, month: number | null, year: number | null): boolean {
		if (!day || !month || !year || !this.endDate) return false;
		const currentDate = new Date(year, month, day);
		return this.areSameDate(currentDate, this.endDate);
	}

	get startDateFormatted(): string {
		if (!this.startDate) return '';
		const year = this.startDate.getFullYear();
		const month = String(this.startDate.getMonth() + 1).padStart(2, '0');
		const day = String(this.startDate.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	get endDateFormatted(): string {
		if (!this.endDate) return '';
		const year = this.endDate.getFullYear();
		const month = String(this.endDate.getMonth() + 1).padStart(2, '0');
		const day = String(this.endDate.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	}

	get startTimeFormatted(): string {
		if (!this.startDate) return '';
		const hours = String(this.startDate.getHours()).padStart(2, '0');
		const minutes = String(this.startDate.getMinutes()).padStart(2, '0');
		return `${hours}:${minutes}`;
	}

	get endTimeFormatted(): string {
		if (!this.endDate) return '';
		const hours = String(this.endDate.getHours()).padStart(2, '0');
		const minutes = String(this.endDate.getMinutes()).padStart(2, '0');
		return `${hours}:${minutes}`;
	}

	getDate(day: number, month: number, year: number): Date | null {
		if (!day || month < 0 || month > 11 || !year) return null;
		return new Date(year, month, day);
	}

	formatDate(date: Date | null): string {
		if (!date) return '';
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		const hour = String(date.getHours()).padStart(2, '0');
		const minute = String(date.getMinutes()).padStart(2, '0');
		return `${year}-${month}-${day}T${hour}:${minute}`;
	}

	getDateRange(): string[] {
		const dateRange = [];
		const start = this.formatDate(this.startDate);
		const end = this.formatDate(this.endDate);
		if (start) dateRange.push(start);
		if (end) dateRange.push(end);
		return dateRange;
	}

	validateDate(dateString: string): boolean {
		// Valida se o valor está no formato correto (YYYY-MM-DD)
		const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
		if (!dateRegex.test(dateString)) {
			return false;
		}

		const [year, month, day] = dateString.split('-').map(Number);

		if (isNaN(year) || isNaN(month) || isNaN(day)) {
			return false;
		}

		if (year < 1900 || year > 2100) {
			return false;
		}

		const selectedDate = new Date(year, month - 1, day);

		if (isNaN(selectedDate.getTime())) {
			return false;
		}

		return selectedDate.getFullYear() === year && selectedDate.getMonth() === month - 1 && selectedDate.getDate() === day;
	}

	onStartDateChange(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		if (value) {
			if (!this.validateDate(value)) return;

			const [year, month, day] = value.split('-').map(Number);
			const selectedDate = new Date(year, month - 1, day);

			const currentMonthDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0); // Último dia do mês atual
			// Impede a seleção de datas futuras
			if (selectedDate <= currentMonthDate) {
				this.startDate = selectedDate;
				this.viewCurrentMonth = this.startDate.getMonth();
				this.viewCurrentYear = this.startDate.getFullYear();
				this.updateCalendar();
			} else {
				this.startDate = null;
				this.updateCalendar();
			}
		} else {
			this.startDate = null;
		}
	}

	onEndDateChange(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		if (value) {
			if (!this.validateDate(value)) return;

			const [year, month, day] = value.split('-').map(Number);
			const selectedDate = new Date(year, month - 1, day);

			this.endDate = selectedDate;
			this.updateCalendar();
		} else {
			this.endDate = null;
		}
	}

	onStartTimeChange(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		// this.startTimeFormatted = value;
		return;
	}

	onEndTimeChange(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		// this.endTimeFormatted = value;
		return;
	}

	confirmDates() {
		if (this.startDate && !this.endDate) {
			this.endDate = new Date(this.startDate);
		}
		if (this.startDate && this.endDate && this.startDate > this.endDate) {
			return;
		}
		this.datesConfirmed.emit(this.getDateRange());
		this.calendarClosed.emit();
	}
}
